import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Glass redesign — Phase 3.
 *
 * TopBar slot system: pages contribute breadcrumb + action content
 * to the persistent `<AppTopBar>` instead of rendering a second
 * header strip below it. The Glass aesthetic relies on a single
 * elevated header line; stacked headers feel cluttered and break
 * the "shell stays still while content swaps" promise.
 *
 * Mechanics:
 *   1. `<TopBarSlotsProvider>` wraps the shell (WorkspaceShell does
 *      this) and owns two ReactNode slots — `left` and `right`.
 *   2. `<AppTopBar>` consumes them via `useTopBarSlots()` and renders
 *      whatever's there. When the slots are empty, the bar still
 *      paints (sidebar trigger + theme toggle remain).
 *   3. Pages call `useSetTopBarSlots({ left, right })` in render —
 *      the hook updates the context and clears on unmount, so
 *      sibling navigations get a clean slate without imperative
 *      teardown at every page.
 *
 * Why a context, not React.Portal: Portal needs the target DOM node
 * mounted before the source renders, which is fine when the layout
 * is stable but flickers during route transitions. Context-based
 * state survives the parent's re-render unchanged and lets us
 * synchronize updates inside React's commit phase.
 *
 * Why a render-time setter (not effect): the slots ARE the page's
 * UI for the bar. Effects run AFTER paint, so the bar would show
 * the previous page's content for one frame on every navigation.
 * Updating during render with the latest content avoids that flash.
 * Cleanup on unmount restores the empty state.
 */

interface TopBarSlotsValue {
  left: ReactNode | null;
  right: ReactNode | null;
  cta: ReactNode | null;
  setLeft: (node: ReactNode | null) => void;
  setRight: (node: ReactNode | null) => void;
  setCta: (node: ReactNode | null) => void;
}

const TopBarSlotsContext = createContext<TopBarSlotsValue | null>(null);

export const TopBarSlotsProvider = ({ children }: { children: ReactNode }) => {
  const [left, setLeft] = useState<ReactNode | null>(null);
  const [right, setRight] = useState<ReactNode | null>(null);
  const [cta, setCta] = useState<ReactNode | null>(null);
  const value = useMemo<TopBarSlotsValue>(
    () => ({ left, right, cta, setLeft, setRight, setCta }),
    [left, right, cta],
  );
  return <TopBarSlotsContext.Provider value={value}>{children}</TopBarSlotsContext.Provider>;
};

/**
 * Read-only access to the current slot contents. Used by AppTopBar.
 * Three slots, rendered in this DOM order:
 *
 *   [left ........] [right] [theme] [cta]
 *
 * The split between `right` and `cta` exists because the prototype's
 * topbar has the primary CTA ("+ New task") sit AFTER the theme toggle
 * — a single slot couldn't express that without reaching into AppTopBar's
 * internal layout. Pages that don't need a CTA simply omit it.
 */
export const useTopBarSlots = (): {
  left: ReactNode | null;
  right: ReactNode | null;
  cta: ReactNode | null;
} => {
  const ctx = useContext(TopBarSlotsContext);
  if (!ctx) return { left: null, right: null, cta: null };
  return { left: ctx.left, right: ctx.right, cta: ctx.cta };
};

/**
 * Page-side hook: contribute slot content. Pass `null` for any slot
 * you don't want to populate. Each named arg is independently
 * tracked, so omitting one doesn't clear what a parent set.
 *
 * Updates fire on every render so derived values (e.g. breadcrumb
 * text from a freshly-loaded query) reach the bar without a lag.
 * Cleanup on unmount returns the slots to null so the next page
 * starts blank.
 */
export const useSetTopBarSlots = ({
  left,
  right,
  cta,
}: {
  left?: ReactNode | null;
  right?: ReactNode | null;
  cta?: ReactNode | null;
}): void => {
  const ctx = useContext(TopBarSlotsContext);

  // Effect-based to avoid setState-during-render warnings. The "old
  // page's content for one frame" problem we'd otherwise hit doesn't
  // apply here: TanStack Router unmounts the old page before mounting
  // the new one, so by the time this effect runs there's no other
  // contributor.
  useEffect(() => {
    if (!ctx) return;
    if (left !== undefined) ctx.setLeft(left);
    if (right !== undefined) ctx.setRight(right);
    if (cta !== undefined) ctx.setCta(cta);
    return () => {
      if (!ctx) return;
      if (left !== undefined) ctx.setLeft(null);
      if (right !== undefined) ctx.setRight(null);
      if (cta !== undefined) ctx.setCta(null);
    };
  }, [ctx, left, right, cta]);
};
